import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Modal from '@/UIlibrary/Modal/Modal';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { Info } from 'react-feather';
import { useSimulationStore } from '@/stores/useSimulationStore';
import { useSimulationTerms } from '@/stores/useSimulationTerms';
import { formatDateTime } from '@/utils/utils';

interface SimulationTermsProps {
	loading: boolean;
	highestAmountToApprove?: string;
	minimumPaymentToSettleTheCreditTotal?: string;
	minimumPaymentToSettleTheCreditPercentage?: string;
	generatedAt?: string;
	startedAt?: string;
}

interface IndicatorTextSectionProps {
	value: string | number;
	title: string;
	loading: boolean;
	onClick: () => void;
	isCalculated?: boolean;
}

const SimulationTerms: React.FC<SimulationTermsProps> = ({
	loading,
	highestAmountToApprove: serverHighestAmount,
	minimumPaymentToSettleTheCreditTotal: serverMinimumTotal,
	minimumPaymentToSettleTheCreditPercentage: serverMinimumPercentage,
	generatedAt
}) => {
	const t = useTranslations('MicrocreditRequestSimulation');
	const [loadingState, setLoadingState] = useState<boolean>(loading);
	const [openInformationModal, setOpenInformationModal] = useState<boolean>(false);
	const [modalTitle, setModalTitle] = useState<string>('HighestAmountToApprove');
	const [modalContent, setModalContent] = useState<string>('HighestAmountToApproveContent');

	const { formData, hasUserInteracted } = useSimulationStore();

	const simulationTerms = useSimulationTerms(formData, {
		highestAmountToApprove: serverHighestAmount,
		minimumPaymentToSettleTheCreditTotal: serverMinimumTotal,
		minimumPaymentToSettleTheCreditPercentage: serverMinimumPercentage
	});

	const hasServerValues = Boolean(
		serverHighestAmount || serverMinimumTotal || serverMinimumPercentage
	);

	const displayValues = formData.sameDayPayment
		? {
				highestAmountToApprove: '-',
				minimumPaymentToSettleTheCreditTotal: '-',
				minimumPaymentToSettleTheCreditPercentage: '-'
			}
		: hasServerValues && !hasUserInteracted
			? {
					highestAmountToApprove: serverHighestAmount,
					minimumPaymentToSettleTheCreditTotal: serverMinimumTotal,
					minimumPaymentToSettleTheCreditPercentage: serverMinimumPercentage
				}
			: simulationTerms.hasCalculatedValues
				? {
						highestAmountToApprove: simulationTerms.highestAmountToApprove,
						minimumPaymentToSettleTheCreditTotal:
							simulationTerms.minimumPaymentToSettleTheCreditTotal,
						minimumPaymentToSettleTheCreditPercentage:
							simulationTerms.minimumPaymentToSettleTheCreditPercentage
					}
				: {
						highestAmountToApprove: serverHighestAmount,
						minimumPaymentToSettleTheCreditTotal: serverMinimumTotal,
						minimumPaymentToSettleTheCreditPercentage: serverMinimumPercentage
					};

	const showClickSimulationToSaveValuesDisclaimer =
		!hasServerValues ||
		simulationTerms.highestAmountToApprove !== serverHighestAmount ||
		simulationTerms.minimumPaymentToSettleTheCreditTotal !== serverMinimumTotal ||
		simulationTerms.minimumPaymentToSettleTheCreditPercentage !== serverMinimumPercentage;

	useEffect(() => {
		setLoadingState(loading);
	}, [loading]);

	const IndicatorTextSection: React.FC<IndicatorTextSectionProps> = ({ value, title, onClick }) => {
		return (
			<div className="w-full py-1 px-4">
				<div className="flex flex-row justify-between">
					<div className="flex flex-row gap-2">
						<Typography size="sm" weight="medium">
							{t(toTranslationKey(title))}:
						</Typography>
						<Info
							color={BRAND_COLORS.brand600}
							size={18}
							onClick={onClick}
							className="cursor-pointer"
						/>
					</div>
					{loading ? (
						<div className="animate-pulse bg-gray-200 h-5 w-1/5 rounded" />
					) : (
						<Typography size="md" weight="semibold">
							{value || '-'}
						</Typography>
					)}
				</div>
			</div>
		);
	};

	return (
		<>
			<div className="px-6">
				<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
					<div className="flex flex-row items-center mb-4">
						<div className="grow">
							<Typography weight="semibold">{t('SimulationTerms')}</Typography>
						</div>
						{formData.sameDayPayment ? (
							<div className="text-xs bg-yellow-50 text-yellow-800 px-3 py-1 rounded-full border border-yellow-200">
								{t('SameDayPaymentDisablesCalculations')}
							</div>
						) : simulationTerms.hasCalculatedValues && hasUserInteracted ? (
							<div className="text-xs bg-blue-50 px-3 py-1 rounded-full">
								{t('LiveCalculations')}
							</div>
						) : null}
						{hasServerValues && !hasUserInteracted && !formData.sameDayPayment && (
							<div className="text-xs bg-blue-50 px-3 py-1 rounded-full">
								{t('LastSimulation')}
								{generatedAt ? `: ${formatDateTime(generatedAt)}` : ''}
							</div>
						)}
					</div>
					<div className="flex flex-col">
						<IndicatorTextSection
							value={displayValues.highestAmountToApprove || '-'}
							title="HighestAmountToApprove"
							loading={loadingState}
							isCalculated={!(hasServerValues && !hasUserInteracted)}
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('HighestAmountToApprove');
								setModalContent('HighestAmountToApproveContent');
							}}
						/>
						<IndicatorTextSection
							value={displayValues.minimumPaymentToSettleTheCreditTotal || '-'}
							title="MinimumPaymentToSettleTheLoan"
							loading={loadingState}
							isCalculated={!(hasServerValues && !hasUserInteracted)}
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('MinimumPaymentToSettleTheLoan');
								setModalContent('MinimumPaymentToSettleTheLoanContent');
							}}
						/>
						<IndicatorTextSection
							value={displayValues.minimumPaymentToSettleTheCreditPercentage || '-'}
							title="MinimumPaymentToSettlePercentageTheLoan"
							loading={loadingState}
							isCalculated={!(hasServerValues && !hasUserInteracted)}
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('MinimumPaymentToSettlePercentageTheLoan');
								setModalContent('MinimumPaymentToSettlePercentageTheLoanContent');
							}}
						/>
					</div>
					{showClickSimulationToSaveValuesDisclaimer && (
						<div className="mt-4">
							<Typography size="sm" weight="medium" color="subtitle">
								* {t('ClickSimulationToSaveValuesDisclaimer')}
							</Typography>
						</div>
					)}
				</div>
			</div>
			<Modal
				isOpen={openInformationModal}
				onClose={() => {
					setOpenInformationModal(false);
				}}
				closeable
				maxWidth={800}
			>
				<div className="flex flex-col gap-4">
					<div className="flex">
						<Typography weight="semibold">{t(toTranslationKey(modalTitle))}</Typography>
					</div>
					<div className="flex flex-col border-y-2 py-4">
						<Typography>{t(toTranslationKey(modalContent))}</Typography>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default SimulationTerms;
